/**
 * Shared query reactivity options and imperative invalidation adapters.
 * Query construction is Effect-only and lives in query-atom-effect.ts.
 */

import { Context, Effect, Layer, Scope } from "effect";
import * as Atom from "effect/unstable/reactivity/Atom";
import * as Reactivity from "effect/unstable/reactivity/Reactivity";

export type QueryAtomOptions<K extends readonly unknown[] = readonly unknown[]> = {
  /** `staleTime` semantics; 0 = always stale (default). */
  readonly staleTime?: number;
  /**
   * 声明式失效键（Atom.withReactivity）：携带相同键的 mutation 成功后，
   * 本查询自动刷新。适用同页失效（mutation 与列表同屏）；
   * 跨页兜底用 staleTime（0 = 挂载即后台重验）。
   * 静态数组或按结构键派生。
   */
  readonly reactivityKeys?:
    | ReadonlyArray<unknown>
    | { readonly [k: string]: ReadonlyArray<unknown> }
    | ((key: K) => ReadonlyArray<unknown> | { readonly [k: string]: ReadonlyArray<unknown> });
};

/**
 * 命令式 reactivity 失效桥：终端 mutation 成功后按键失效所有
 * withReactivity 订阅者。
 * 用法（React 侧）：`const invalidate = useAtomSet(reactivityInvalidateAtom)`
 * 然后 `invalidate(["knowledgeEntry"])`。
 */
// 与 withReactivity 共享 defaultMemoMap → 同一个 Reactivity 实例
const reactivityRuntime = Atom.runtime(Layer.empty);

export const reactivityInvalidateAtom = reactivityRuntime.fn<
  ReadonlyArray<unknown> | { readonly [k: string]: ReadonlyArray<unknown> }
>()((keys) => Reactivity.invalidate(keys));

/**
 * 非 React 上下文的声明式失效：直接在 Effect 工作流或终端适配器中调用，
 * 无需 useAtomSet。与 reactivityInvalidateAtom / Atom.withReactivity 共享
 * 同一个 Reactivity 实例（通过 Atom.defaultMemoMap 保证单例）。
 */
const _reactivityCtx = Effect.runSync(
  Effect.flatMap(Scope.make(), (scope) =>
    Layer.buildWithMemoMap(Atom.defaultMemoMap, scope)(Reactivity.layer),
  ),
);
const _invalidateUnsafe = Context.get(_reactivityCtx, Reactivity.Reactivity).invalidateUnsafe;

export function invalidateReactivityKeys(
  keys: ReadonlyArray<unknown> | { readonly [k: string]: ReadonlyArray<unknown> },
): void {
  _invalidateUnsafe(keys);
}
